import { pgTable, text, timestamp, integer, index, uniqueIndex, foreignKey, jsonb, numeric } from 'drizzle-orm/pg-core';
import { sql } from 'drizzle-orm';
import { user } from './user';
import { presenceSource } from './enums';

export const userSession = pgTable('UserSession', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  visitorId: text().notNull(),
  userId: integer(),
  startedAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
  endedAt: timestamp({ precision: 3, mode: 'date' }),
  ip: text(),
  userAgent: text(),
  device: text(),
  browser: text(),
  os: text(),
  country: text(),
  city: text(),
  createdAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
  updatedAt: timestamp({ precision: 3, mode: 'date' }).notNull(),
}, (table) => [
  index('UserSession_startedAt_idx').using('btree', table.startedAt.asc().nullsLast()),
  index('UserSession_userId_idx').using('btree', table.userId.asc().nullsLast()),
  index('UserSession_visitorId_idx').using('btree', table.visitorId.asc().nullsLast()),
  foreignKey({
    columns: [table.userId],
    foreignColumns: [user.id],
    name: 'UserSession_userId_fkey'
  }).onUpdate('cascade').onDelete('set null'),
]);

export const userAction = pgTable('UserAction', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  sessionId: integer().notNull(),
  userId: integer(),
  type: text().notNull(),
  page: text(),
  component: text(),
  payload: jsonb(),
  createdAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
}, (table) => [
  index('UserAction_createdAt_idx').using('btree', table.createdAt.asc().nullsLast()),
  index('UserAction_page_idx').using('btree', table.page.asc().nullsLast()),
  index('UserAction_sessionId_idx').using('btree', table.sessionId.asc().nullsLast()),
  index('UserAction_type_idx').using('btree', table.type.asc().nullsLast()),
  index('UserAction_userId_idx').using('btree', table.userId.asc().nullsLast()),
  foreignKey({
    columns: [table.sessionId],
    foreignColumns: [userSession.id],
    name: 'UserAction_sessionId_fkey'
  }).onUpdate('cascade').onDelete('cascade'),
  foreignKey({
    columns: [table.userId],
    foreignColumns: [user.id],
    name: 'UserAction_userId_fkey'
  }).onUpdate('cascade').onDelete('set null'),
]);

/**
 * UserPresenceLog — infinite online history.
 *
 * Каждая строка описывает непрерывный интервал «юзер был онлайн», источник
 * (WEB / BOT / MINI_APP), и последний heartbeat. `endedAt = null` означает,
 * что интервал всё ещё открыт (юзер сейчас онлайн); фоновый cron-worker
 * закрывает stale-строки (`endedAt = lastHeartbeatAt`), если heartbeat не
 * приходил > 5 минут.
 *
 * Партиционирование (по месяцу через `CREATE TABLE ... PARTITION OF ...`)
 * описано в отдельной template-миграции и пока не применено — при росте
 * объёма включается вручную. Для MVP достаточно plain таблицы + индексов.
 */
export const userPresenceLog = pgTable('UserPresenceLog', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  userId: integer().notNull(),
  startedAt: timestamp({ precision: 3, mode: 'date' }).notNull(),
  endedAt: timestamp({ precision: 3, mode: 'date' }),
  lastHeartbeatAt: timestamp({ precision: 3, mode: 'date' }).notNull(),
  source: presenceSource().notNull(),
  sessionId: integer(),
  createdAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
}, (table) => [
  index('UserPresenceLog_userId_startedAt_idx').using('btree', table.userId.asc().nullsLast(), table.startedAt.asc().nullsLast()),
  index('UserPresenceLog_startedAt_idx').using('btree', table.startedAt.asc().nullsLast()),
  index('UserPresenceLog_lastHeartbeatAt_idx').using('btree', table.lastHeartbeatAt.asc().nullsLast()),
  index('UserPresenceLog_endedAt_idx').using('btree', table.endedAt.asc().nullsLast()),
  foreignKey({
    columns: [table.userId],
    foreignColumns: [user.id],
    name: 'UserPresenceLog_userId_fkey',
  }).onUpdate('cascade').onDelete('cascade'),
]);

export const dailyStats = pgTable('DailyStats', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  date: timestamp({ precision: 3, mode: 'date' }).notNull(),
  totalUsers: integer().default(0).notNull(),
  newUsers: integer().default(0).notNull(),
  activeUsers: integer().default(0).notNull(),
  activeSubscriptions: integer().default(0).notNull(),
  newSubscriptions: integer().default(0).notNull(),
  expiredSubscriptions: integer().default(0).notNull(),
  revenue: numeric({ precision: 12, scale: 2 }).default('0').notNull(),
  mrr: numeric({ precision: 12, scale: 2 }).default('0').notNull(),
  churnRate: numeric({ precision: 5, scale: 2 }).default('0').notNull(),
  peakOnlineUsers: integer().default(0).notNull(),
  createdAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
}, (table) => [
  index('DailyStats_date_idx').using('btree', table.date.asc().nullsLast()),
  uniqueIndex('DailyStats_date_key').using('btree', table.date.asc().nullsLast()),
]);
